import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { TID } from "../constants/testIds";
import { MapPin, Layers, Shield, Calculator, Building2, ArrowRight, Compass } from "lucide-react";
import { LogoMark } from "../components/Logo";

const modules = [
  { icon: Layers, title: "Growth Radar", desc: "STRR, PRR, Metro Blue Line, and Tech Corridor overlays across Bangalore." },
  { icon: Building2, title: "Parcel Repository", desc: "40+ curated parcels with owner category, khata, and conversion filters." },
  { icon: Shield, title: "10-Point Legal Scorecard", desc: "Mother-deed, EC, podi, and litigation audit by in-house advocates." },
  { icon: Calculator, title: "JD & FAR Calculator", desc: "Instant permissible FAR and 50:50 / 60:40 revenue split recommendations." },
];

const microMarkets = [
  { name: "Devanahalli", tag: "Airport Growth", price: "₹1.5–3.5 Cr/Acre" },
  { name: "Sarjapur", tag: "IT Corridor", price: "₹2.5–6 Cr/Acre" },
  { name: "Dommasandra", tag: "Nice Ring", price: "₹1.8–4 Cr/Acre" },
  { name: "Hoskote", tag: "East Bangalore", price: "₹1.2–2.8 Cr/Acre" },
  { name: "Bidadi", tag: "Mysuru Highway", price: "₹1.0–2.2 Cr/Acre" },
  { name: "Whitefield", tag: "Established", price: "₹3.5–7.5 Cr/Acre" },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="relative bg-[#0F172A] text-[#F8FAFC]">
      {/* HERO */}
      <section data-testid={TID.landingHero} className="relative overflow-hidden border-b border-[#334155]">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#10B981]/10 blur-3xl" />
        <div className="pointer-events-none hidden lg:block absolute right-12 top-14 opacity-25">
          <LogoMark size={220} color="#10B981" animated />
        </div>
        <div className="mx-auto max-w-[1600px] px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[#10B981]">
              <LogoMark size={18} color="#10B981" animated /> Live Corridor Intelligence
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              Map Bangalore's next<br />
              <span className="text-[#10B981]">urban growth vector.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-400 max-w-xl leading-relaxed">
              AcrePulse tracks developer land-banks, infrastructure pipelines, and legal readiness across the STRR, PRR,
              and Airport Metro corridors — so investors and HNIs commit capital with conviction.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button data-testid={TID.ctaExploreMap} onClick={() => navigate("/map")} className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white h-12 px-8 text-base">
                Explore the Growth Map <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button data-testid={TID.ctaGetStarted} variant="outline" onClick={() => navigate("/signup")} className="rounded-full h-12 px-8 border-[#334155] bg-transparent text-white hover:bg-[#1E293B] hover:text-white">
                Get Investor Access
              </Button>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "42+", v: "Parcels" },
                { k: "8", v: "Infra Projects" },
                { k: "6", v: "Micro-markets" },
              ].map((s) => (
                <div key={s.v} className="border-l-2 border-[#10B981] pl-4">
                  <div className="font-display text-3xl font-bold">{s.k}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 fade-up">
            <div className="glass rounded-2xl overflow-hidden">
              <img alt="Bangalore skyline" className="w-full h-72 object-cover"
                src="https://images.unsplash.com/photo-1737450768947-30d0abebe63e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxCYW5nYWxvcmUlMjBjaXR5JTIwc2t5bGluZSUyMHN1bnNldHxlbnwwfHx8fDE3ODU0MjU1NTV8MA&ixlib=rb-4.1.0&q=85" />
              <div className="p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Featured Corridor</div>
                <h3 className="mt-2 font-display text-2xl font-semibold">STRR — Satellite Town Ring Road</h3>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">A 280 km outer ring linking Doddaballapur, Devanahalli, Hoskote, Sarjapur and Bidadi. Highlighted in Pulse Green across the map.</p>
                <div className="mt-4 flex items-center gap-3">
                  <span className="rounded-full bg-[#10B981]/15 text-[#10B981] text-xs px-3 py-1 border border-[#10B981]/30">Under Construction</span>
                  <span className="text-xs text-slate-500">ETA 2027</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 border-b border-[#334155]">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">Modules</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Every tool a land-side principal needs.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {modules.map((m, i) => (
            <div key={m.title} style={{ animationDelay: `${i * 80}ms` }} className="fade-up rounded-xl border border-[#334155] bg-[#1E293B] p-6 hover:-translate-y-1 hover:border-[#10B981] transition-transform duration-300">
              <m.icon className="h-6 w-6 text-[#10B981]" />
              <h3 className="mt-4 font-display text-lg font-semibold">{m.title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MICRO MARKETS */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 border-b border-[#334155]">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">Micro-markets</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">High-conviction corridors we track weekly.</h2>
          </div>
          <Button variant="outline" onClick={() => navigate("/map")} className="rounded-full border-[#334155] bg-transparent text-white hover:bg-[#1E293B] hover:text-white">
            Open Live Map <Compass className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {microMarkets.map((mm) => (
            <div key={mm.name} className="group relative overflow-hidden rounded-xl border border-[#334155] bg-[#1E293B] p-6 hover:border-[#10B981] transition-colors">
              <MapPin className="h-5 w-5 text-[#10B981]" />
              <h3 className="mt-3 font-display text-xl font-semibold">{mm.name}</h3>
              <div className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500">{mm.tag}</div>
              <div className="mt-6 text-[#10B981] font-medium">{mm.price}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="mx-auto max-w-[1600px] px-6 py-20">
        <div className="rounded-2xl border border-[#334155] bg-gradient-to-br from-[#1E293B] to-[#0F172A] p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight max-w-xl">Book a 1-on-1 with our land advocates.</h2>
            <p className="mt-3 text-slate-400 text-sm max-w-lg">Legal audit, JD structuring, and corridor strategy — all in a single session.</p>
          </div>
          <Button onClick={() => navigate("/bookings")} className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white h-12 px-8">
            Book Advisory <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <div className="mt-10 text-center text-xs text-slate-500 tracking-[0.15em] uppercase">© 2026 AcrePulse · Bangalore Land Intelligence</div>
      </section>
    </div>
  );
}
